import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ShieldCheck,
  Lock,
  Scale,
  FileText,
  Fingerprint,
  Cpu,
  Workflow,
  CheckCircle2,
  Clock,
  CircleDot,
  Download,
  ArrowRight,
  Building2,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";
import {
  PROGRAMME_STATUS,
  CERTIFICATIONS,
  ARCHITECTURE_LAYERS,
  DOC_CATEGORIES,
  REQUEST_TYPES,
  TRUST_FAQ,
} from "@/data/trustCentre";

const STATUS_STYLES = {
  active: {
    dot: "#22C55E",
    bg: "rgba(34,197,94,0.10)",
    border: "rgba(34,197,94,0.30)",
    text: "#15803D",
  },
  progress: {
    dot: "#F97316",
    bg: "rgba(249,115,22,0.10)",
    border: "rgba(249,115,22,0.30)",
    text: "#C2410C",
  },
  planned: {
    dot: "#3B82F6",
    bg: "rgba(59,130,246,0.10)",
    border: "rgba(59,130,246,0.30)",
    text: "#1D4ED8",
  },
  neutral: {
    dot: "#6B7280",
    bg: "rgba(107,114,128,0.10)",
    border: "rgba(107,114,128,0.30)",
    text: "#374151",
  },
};

const statusToneFor = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("active") || s === "available") return "active";
  if (s.includes("planned")) return "planned";
  if (
    s.includes("progress") ||
    s.includes("underway") ||
    s.includes("ongoing") ||
    s.includes("development") ||
    s.includes("review") ||
    s.includes("coming")
  )
    return "progress";
  return "neutral";
};

const StatusBadge = ({ status, tone }) => {
  const t = STATUS_STYLES[tone || statusToneFor(status)];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] uppercase tracking-[0.14em] font-semibold"
      style={{
        backgroundColor: t.bg,
        border: `1px solid ${t.border}`,
        color: t.text,
      }}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: t.dot }}
      />
      {status}
    </span>
  );
};

const ARCH_ICONS = {
  identity: Fingerprint,
  hardware: Cpu,
  governance: Workflow,
};

const initialForm = {
  name: "",
  company: "",
  role: "",
  email: "",
  request_type: REQUEST_TYPES[0],
  message: "",
};

export default function TrustCentre() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: form.name,
        company: form.company,
        role: form.role,
        email: form.email,
        area_of_interest: `Trust Centre · ${form.request_type}`,
        message: form.message,
      });
      setSuccess(true);
      toast.success("Request received. The Vaultavo team will be in touch shortly.");
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or contact us at hello@vaultavo.com.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-white" data-testid="trust-centre-page">
      <Toaster position="top-center" richColors />

      {/* SECTION 1 — HERO */}
      <section className="pt-32 md:pt-40 pb-20 bg-radial-soft relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
        <div
          className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 60%)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
              Trust Centre
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-medium leading-[1.02] tracking-tight text-[#06102A] max-w-5xl">
              Built on a foundation of security, compliance and trust.
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-7 max-w-2xl text-lg text-[#06102A]/70">
              Vaultavo provides identity-bound custody infrastructure designed for institutions,
              regulated financial entities, and the future of digital ownership.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <a
                href="#documentation-requests"
                data-testid="trust-cta-request-documentation"
                className="group inline-flex items-center gap-2 rounded-full bg-[#06102A] text-white text-sm font-medium px-6 py-3 hover:-translate-y-0.5 transition-all duration-300 shadow-[0_8px_24px_rgba(6,16,42,0.18)]"
              >
                Request Documentation
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <Link
                to="/contact?area=Security"
                data-testid="trust-cta-contact-security"
                className="inline-flex items-center gap-2 rounded-full border border-[#06102A]/20 text-[#06102A] text-sm font-medium px-6 py-3 hover:border-[#06102A] transition-all"
              >
                Contact Security Team
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 2 — PROGRAMME STATUS */}
      <section
        className="py-24 md:py-32 bg-white"
        data-testid="trust-programme-status"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <Reveal>
                <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
                  Programme Status
                </p>
              </Reveal>
              <Reveal delay={80}>
                <h2 className="mt-5 font-display text-4xl md:text-5xl font-medium leading-tight text-[#06102A] max-w-7xl">
                  Security and compliance programmes, transparently disclosed.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <p className="font-mono-vt text-xs text-[#06102A]/55">
                Last updated · {new Date().toLocaleString("en-GB", { month: "short", year: "numeric" })}
              </p>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PROGRAMME_STATUS.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 80}>
                <article
                  data-testid={`programme-status-${p.id}`}
                  className="group h-full rounded-2xl border border-[#06102A]/10 bg-white p-7 hover:border-[#06102A]/40 hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-24px_rgba(6,16,42,0.18)] transition-all duration-500"
                >
                  <div className="flex items-start justify-between gap-4">
                    <StatusBadge status={p.status} tone={p.tone} />
                    <span className="font-mono-vt text-xs text-[#06102A]/40">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="mt-6 font-display text-xl font-medium text-[#06102A] leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#06102A]/70 leading-relaxed">
                    {p.desc}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — CERTIFICATIONS */}
      <section
        className="py-24 md:py-32 bg-[#FAFBFC]"
        data-testid="trust-certifications"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
              Certifications & Assurance
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-medium leading-tight text-[#06102A] max-w-7xl">
              The certification roadmap towards institutional readiness.
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-6 max-w-7xl text-[15px] text-[#06102A]/65 leading-relaxed">
              Vaultavo is progressing a comprehensive certification and assurance roadmap. Status is updated as each programme advances.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CERTIFICATIONS.map((c, i) => (
              <Reveal key={c.id} delay={(i % 3) * 70}>
                <article
                  data-testid={`certification-${c.id}`}
                  className="group relative h-full rounded-2xl border border-[#06102A]/10 bg-white p-7 overflow-hidden hover:border-[#06102A]/40 hover:-translate-y-0.5 transition-all duration-500"
                >
                  {/* metallic accent */}
                  <div
                    className="absolute -top-px left-7 right-7 h-px pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(90deg, transparent, rgba(6,16,42,0.35), transparent)",
                    }}
                  />
                  <div className="flex items-start justify-between gap-4">
                    {/* metallic badge */}
                    <div
                      className="grid place-items-center h-12 w-12 rounded-xl"
                      style={{
                        background:
                          "radial-gradient(circle at 30% 25%, #FFFFFF 0%, #E5E7EB 55%, #B8BDC8 100%)",
                        boxShadow:
                          "inset 0 -4px 10px rgba(6,16,42,0.14), inset 0 3px 6px rgba(255,255,255,0.9), 0 6px 16px -8px rgba(6,16,42,0.22)",
                      }}
                    >
                      <ShieldCheck
                        className="h-5 w-5 text-[#06102A]"
                        strokeWidth={1.6}
                      />
                    </div>
                    <StatusBadge status={c.status} />
                  </div>
                  <h3 className="mt-7 font-display text-xl font-medium text-[#06102A] leading-tight">
                    {c.name}
                  </h3>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#06102A]/55">
                    {c.family}
                  </p>
                  <p className="mt-4 text-sm text-[#06102A]/70 leading-relaxed">
                    {c.desc}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — SECURITY ARCHITECTURE */}
      <section
        className="py-24 md:py-32 dark-panel relative overflow-hidden"
        data-testid="trust-architecture"
      >
        <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
        <div
          className="absolute -bottom-40 -left-40 h-[600px] w-[600px] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(59,130,246,0.18) 0%, transparent 60%)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-white/65">
              Security Architecture
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-medium leading-tight text-white max-w-7xl">
              Security at every layer.
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-6 max-w-7xl text-[15px] text-white/65 leading-relaxed">
              Three independent layers. One unified system. Compromising any single layer is not
              sufficient to compromise custody.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4">
            {ARCHITECTURE_LAYERS.map((layer, i) => {
              const Icon = ARCH_ICONS[layer.id] || Lock;
              return (
                <Reveal key={layer.id} delay={i * 100}>
                  <article
                    data-testid={`architecture-layer-${layer.id}`}
                    className="h-full rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-7 hover:bg-white/[0.07] hover:border-white/25 transition-all duration-500"
                  >
                    <div className="flex items-center justify-between">
                      <div className="grid place-items-center h-11 w-11 rounded-xl bg-white/10 text-white">
                        <Icon className="h-5 w-5" strokeWidth={1.6} />
                      </div>
                      <span className="font-mono-vt text-xs text-white/45">
                        {layer.label}
                      </span>
                    </div>
                    <h3 className="mt-7 font-display text-2xl font-medium text-white leading-tight">
                      {layer.title}
                    </h3>
                    <p className="mt-3 text-sm text-white/70 leading-relaxed">
                      {layer.desc}
                    </p>
                    <ul className="mt-6 space-y-2.5 border-t border-white/10 pt-5">
                      {layer.points.map((p) => (
                        <li
                          key={p}
                          className="flex items-start gap-3 text-[13px] text-white/75"
                        >
                          <CheckCircle2 className="h-4 w-4 text-[#3B82F6] mt-0.5 flex-shrink-0" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </article>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={200}>
            <div className="mt-12">
              <Link
                to="/technology"
                data-testid="trust-architecture-tech-link"
                className="inline-flex items-center gap-2 text-sm font-medium text-white hover:text-[#3B82F6] transition-colors group"
              >
                Explore the full technology stack
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 5 — DOCUMENTATION LIBRARY */}
      <section
        className="py-24 md:py-32 bg-white"
        data-testid="trust-documentation"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
              Documentation Library
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-medium leading-tight text-[#06102A] max-w-7xl">
              Trust documentation, organised for procurement teams.
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4">
            {DOC_CATEGORIES.map((cat, i) => (
              <Reveal key={cat.id} delay={(i % 2) * 90}>
                <article
                  data-testid={`doc-category-${cat.id}`}
                  className="rounded-2xl border border-[#06102A]/10 bg-white p-7 md:p-8 hover:border-[#06102A]/30 transition-all duration-500"
                >
                  <div className="flex items-center justify-between border-b border-[#06102A]/10 pb-5">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-[#06102A] grid place-items-center text-white">
                        <FileText className="h-4 w-4" strokeWidth={1.7} />
                      </div>
                      <h3 className="font-display text-xl font-medium text-[#06102A]">
                        {cat.title}
                      </h3>
                    </div>
                    <span className="font-mono-vt text-xs text-[#06102A]/40">
                      {String(cat.items.length).padStart(2, "0")} docs
                    </span>
                  </div>
                  <ul className="mt-2 divide-y divide-[#06102A]/[0.07]">
                    {cat.items.map((item) => {
                      const isLink = item.status === "Available" && item.href;
                      const Inner = (
                        <div className="flex items-center justify-between gap-4 py-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="text-[#06102A]/40">
                              {item.status === "Available" ? (
                                <CheckCircle2 className="h-4 w-4 text-[#22C55E]" />
                              ) : item.status === "Coming Soon" ? (
                                <Clock className="h-4 w-4" />
                              ) : (
                                <CircleDot className="h-4 w-4" />
                              )}
                            </span>
                            <p className="text-[14.5px] text-[#06102A] truncate">
                              {item.name}
                            </p>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <StatusBadge status={item.status} />
                            {isLink && (
                              <ArrowUpRight className="h-4 w-4 text-[#06102A]/45" />
                            )}
                          </div>
                        </div>
                      );
                      return (
                        <li key={item.name}>
                          {isLink ? (
                            <Link
                              to={item.href}
                              data-testid={`doc-item-${item.name
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, "-")}`}
                              className="block hover:bg-[#06102A]/[0.02] -mx-3 px-3 rounded-lg transition-colors"
                            >
                              {Inner}
                            </Link>
                          ) : (
                            <div
                              data-testid={`doc-item-${item.name
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, "-")}`}
                            >
                              {Inner}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  {cat.id !== "privacy-legal" && (
                    <a
                      href="#documentation-requests"
                      className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#06102A] hover:text-[#3B82F6] transition-colors group"
                    >
                      Request documents in this category
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </a>
                  )}
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — REQUEST FORM */}
      <section
        id="documentation-requests"
        className="py-24 md:py-32 bg-[#FAFBFC] scroll-mt-24"
        data-testid="trust-request-form-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 lg:sticky lg:top-28 lg:self-start">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
                Documentation Requests
              </p>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="mt-5 font-display text-4xl md:text-5xl font-medium leading-tight text-[#06102A]">
                Audit, due diligence, and procurement enquiries.
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-6 text-[15px] text-[#06102A]/70 leading-relaxed">
                Submit a request for audit packs, security reviews, due diligence documentation, or
                procurement support. Sensitive materials are released under a mutual non-disclosure
                agreement. Standard turnaround is five business days.
              </p>
            </Reveal>
            <Reveal delay={200}>
              <div className="mt-8 space-y-3">
                {[
                  { icon: Building2, t: "For institutional buyers" },
                  { icon: Scale, t: "Compliance, legal, and risk teams" },
                  { icon: Lock, t: "Released under NDA where required" },
                ].map((row) => (
                  <div key={row.t} className="flex items-center gap-3 text-[14px] text-[#06102A]/80">
                    <row.icon className="h-4 w-4 text-[#06102A]/60" strokeWidth={1.7} />
                    {row.t}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <form
                onSubmit={submit}
                data-testid="trust-request-form"
                className="rounded-3xl border border-[#06102A]/10 bg-white p-8 md:p-10 shadow-[0_30px_60px_-40px_rgba(6,16,42,0.25)]"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field
                    label="Name"
                    testid="trust-input-name"
                    value={form.name}
                    onChange={update("name")}
                    required
                    placeholder="Full name"
                  />
                  <Field
                    label="Company"
                    testid="trust-input-company"
                    value={form.company}
                    onChange={update("company")}
                    required
                    placeholder="Institution"
                  />
                  <Field
                    label="Role"
                    testid="trust-input-role"
                    value={form.role}
                    onChange={update("role")}
                    required
                    placeholder="Title"
                  />
                  <Field
                    label="Email"
                    testid="trust-input-email"
                    value={form.email}
                    onChange={update("email")}
                    required
                    type="email"
                    placeholder="name@institution.com"
                  />
                </div>

                <div className="mt-5">
                  <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
                    Request Type
                  </p>

                  {/* Mobile: styled select */}
                  <div className="mt-3 sm:hidden relative">
                    <select
                      data-testid="trust-request-type-select"
                      value={form.request_type}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, request_type: e.target.value }))
                      }
                      className="w-full appearance-none rounded-xl border border-[#06102A]/15 bg-white px-4 py-3 pr-10 text-[15px] text-[#06102A] focus:outline-none focus:border-[#06102A] focus:ring-2 focus:ring-[#06102A]/10 transition-all"
                    >
                      {REQUEST_TYPES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                    <svg
                      aria-hidden="true"
                      className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#06102A]/55"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>

                  {/* Tablet+ : pill row */}
                  <div className="mt-3 hidden sm:flex flex-wrap gap-2">
                    {REQUEST_TYPES.map((r) => {
                      const active = form.request_type === r;
                      return (
                        <button
                          type="button"
                          key={r}
                          data-testid={`trust-request-type-${r
                            .toLowerCase()
                            .replace(/\s+/g, "-")}`}
                          onClick={() =>
                            setForm((f) => ({ ...f, request_type: r }))
                          }
                          className={`rounded-full px-4 py-2 text-xs font-medium border transition-all ${
                            active
                              ? "bg-[#06102A] text-white border-[#06102A]"
                              : "bg-white text-[#06102A]/75 border-[#06102A]/15 hover:border-[#06102A]"
                          }`}
                        >
                          {r}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-5">
                  <label className="block">
                    <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
                      Message
                    </span>
                    <textarea
                      data-testid="trust-input-message"
                      required
                      value={form.message}
                      onChange={update("message")}
                      rows={5}
                      placeholder="Describe the documentation required, intended audience, and any procurement deadlines."
                      className="mt-3 w-full rounded-xl border border-[#06102A]/15 bg-white px-4 py-3 text-[15px] text-[#06102A] placeholder:text-[#06102A]/35 focus:outline-none focus:border-[#06102A] focus:ring-2 focus:ring-[#06102A]/10 transition-all resize-none"
                    />
                  </label>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-xs text-[#06102A]/55 max-w-sm">
                    Submissions are reviewed by the Vaultavo security and compliance team only.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="trust-submit"
                    className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#06102A] text-white text-sm font-medium px-6 py-3 hover:-translate-y-0.5 transition-all disabled:opacity-60"
                  >
                    {submitting ? "Sending…" : "Submit request"}
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                </div>

                {success && (
                  <div
                    data-testid="trust-success"
                    className="mt-6 flex items-start gap-3 rounded-xl border border-[#3B82F6]/30 bg-[#3B82F6]/[0.06] p-4"
                  >
                    <CheckCircle2 className="h-5 w-5 text-[#3B82F6] mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-[#06102A]">
                        Request received.
                      </p>
                      <p className="text-xs text-[#06102A]/70 mt-1">
                        The Vaultavo security & compliance team will respond within five business
                        days.
                      </p>
                    </div>
                  </div>
                )}
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 7 — TRUST CENTRE FAQ */}
      <section className="py-24 md:py-32 bg-white" data-testid="trust-faq">
        <div className="max-w-7xl mx-auto px-6 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
                Trust & Security FAQ
              </p>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="mt-5 font-display text-4xl md:text-5xl font-medium leading-tight text-[#06102A]">
                Answers for buyers, auditors, and regulators.
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-6 text-[15px] text-[#06102A]/70 leading-relaxed">
                Not what you're looking for?{" "}
                <Link
                  to="/faq"
                  className="text-[#06102A] underline underline-offset-4 decoration-[#06102A]/30 hover:decoration-[#06102A]"
                >
                  Browse the full FAQ
                </Link>
                {" "}or contact the security team below.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <Reveal>
              <Accordion
                type="single"
                collapsible
                className="border-t border-[#06102A]/10"
                data-testid="trust-faq-accordion"
              >
                {TRUST_FAQ.map((it, i) => (
                  <AccordionItem
                    key={i}
                    value={`item-${i}`}
                    className="border-b border-[#06102A]/10"
                    data-testid={`trust-faq-item-${i}`}
                  >
                    <AccordionTrigger className="text-left text-[#06102A] hover:text-[#06102A] hover:no-underline py-5 text-[15px] md:text-base font-medium">
                      {it.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[14.5px] text-[#06102A]/75 leading-relaxed pb-6 pr-2">
                      {it.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 8 — UNIFIED CONTACT */}
      <section
        className="py-24 md:py-32 bg-[#FAFBFC]"
        data-testid="trust-security-contact"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
              Direct Contact
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-medium leading-tight text-[#06102A] max-w-7xl">
              Contact the security &amp; compliance team.
            </h2>
          </Reveal>

          <div className="mt-14">
            <Reveal>
              <article
                data-testid="trust-contact-unified"
                className="group relative rounded-3xl border border-[#06102A]/10 bg-white p-8 md:p-12 hover:border-[#06102A]/30 hover:shadow-[0_30px_60px_-30px_rgba(6,16,42,0.18)] transition-all duration-500 overflow-hidden"
              >
                <div
                  className="absolute -top-px left-12 right-12 h-px pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, rgba(59,130,246,0.5), transparent)",
                  }}
                />

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                  <div className="lg:col-span-7">
                    <div className="flex items-center gap-3">
                      <div className="h-11 w-11 rounded-xl bg-[#06102A] text-white grid place-items-center">
                        <ShieldCheck className="h-5 w-5" strokeWidth={1.6} />
                      </div>
                      <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#F97316]">
                        Security &amp; Compliance
                      </p>
                    </div>

                    <p className="mt-7 text-[15px] md:text-base text-[#06102A]/75 leading-relaxed">
                      For security enquiries, compliance requests, audit documentation, due
                      diligence reviews, certification enquiries, vulnerability reporting, or
                      regulatory questions, please contact the Vaultavo team through our secure
                      contact process.
                    </p>

                    <a
                      href="mailto:hello@vaultavo.com"
                      data-testid="trust-contact-email-link"
                      className="mt-8 inline-flex items-center gap-2 font-display text-xl md:text-2xl font-medium text-[#06102A] hover:text-[#3B82F6] transition-colors"
                    >
                      hello@vaultavo.com
                      <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>

                    <div className="mt-10 flex flex-wrap items-center gap-3">
                      <Link
                        to="/contact?area=Documentation+Request"
                        data-testid="trust-contact-cta-primary"
                        className="group inline-flex items-center gap-2 rounded-full bg-[#06102A] text-white text-sm font-medium px-6 py-3 hover:-translate-y-0.5 transition-all shadow-[0_8px_24px_rgba(6,16,42,0.18)]"
                      >
                        Submit an Enquiry
                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </Link>
                      <a
                        href="#documentation-requests"
                        data-testid="trust-contact-cta-docs"
                        className="inline-flex items-center gap-2 rounded-full border border-[#06102A]/20 text-[#06102A] text-sm font-medium px-6 py-3 hover:border-[#06102A] transition-all"
                      >
                        Request Documentation
                      </a>
                    </div>
                  </div>

                  <div className="lg:col-span-5 lg:border-l lg:border-[#06102A]/10 lg:pl-12">
                    <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
                      Enquiry Categories
                    </p>
                    <p className="mt-4 text-[14px] text-[#06102A]/65 leading-relaxed">
                      Please select the relevant category so requests are routed internally to the
                      appropriate team.
                    </p>
                    <ul className="mt-6 space-y-3">
                      {[
                        "Security",
                        "Compliance",
                        "Documentation Request",
                        "Audit & Due Diligence",
                        "General Enquiry",
                      ].map((cat) => (
                        <li
                          key={cat}
                          data-testid={`trust-contact-category-${cat
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, "-")}`}
                          className="flex items-center gap-3 text-[14.5px] text-[#06102A]"
                        >
                          <CheckCircle2 className="h-4 w-4 text-[#3B82F6] flex-shrink-0" />
                          {cat}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            </Reveal>
          </div>

          <Reveal delay={150}>
            <div className="mt-10 rounded-2xl border border-[#06102A]/10 bg-white p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-xl bg-[#06102A]/[0.04] grid place-items-center flex-shrink-0">
                  <Download className="h-4 w-4 text-[#06102A]" strokeWidth={1.7} />
                </div>
                <div>
                  <p className="font-display text-lg font-medium text-[#06102A]">
                    Need procurement documentation now?
                  </p>
                  <p className="mt-1 text-[14px] text-[#06102A]/65">
                    Submit a documentation request and receive available materials within five
                    business days.
                  </p>
                </div>
              </div>
              <a
                href="#documentation-requests"
                data-testid="trust-bottom-request-cta"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#06102A] text-white text-sm font-medium px-6 py-3 hover:-translate-y-0.5 transition-all whitespace-nowrap"
              >
                Open Request Form
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

const Field = ({
  label,
  testid,
  value,
  onChange,
  required,
  type = "text",
  placeholder,
}) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
      {label}
    </span>
    <input
      data-testid={testid}
      value={value}
      onChange={onChange}
      required={required}
      type={type}
      placeholder={placeholder}
      className="mt-3 w-full rounded-xl border border-[#06102A]/15 bg-white px-4 py-3 text-[15px] text-[#06102A] placeholder:text-[#06102A]/35 focus:outline-none focus:border-[#06102A] focus:ring-2 focus:ring-[#06102A]/10 transition-all"
    />
  </label>
);
